import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Icon, Input, Pagination, Radio, Spin, Tabs, Tooltip } from "antd";
import axios from "axios";
import CommonHeader from "../../CommonHeader";
import CustomHeader from "../../../../CommonHeader/CustomHeader";
import CustomAgGridTable from "../../../../Common-Table/CustomAgGridTable";
import BadNumberTooltip from "../../../../Custom-Tooltip/BadNumberTooltip";
import {
  EA,
  FOLLOW_UP_SPECIALIST,
  FOLLOWUP,
  LEAD,
  OPPORTUNITY,
  PROSPECT,
  REVIEW,
  REVIEW_SCHEDULER,
  REVIEWADMIN,
  TYPE_TO_FILTER_BY_NAME,
} from "../../../../utils/Constants";
import {
  GET_FS_ALL_AGENCIES,
  RS_ALL_AGENCIES_API_URL,
  WEEKLY_DASH_ACTIVE1_GRID,
  WEEKLY_DASH_ACTIVE2_GRID,
  WEEKLY_DASH_DUE_CALLS_GRID,
  WEEKLY_DASH_STAT_GRID,
} from "../../../../utils/Api_enums";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import "../../../../Common-Table/CustomAgGridTable.css";
import "./styles/style.css";
import moment from "moment";
import F from "../../../../Functions";
import { useBoundStore } from "../../../../store/useBoundStore";

const PAGE_SIZE = 10;
const { TabPane } = Tabs;

const TOTAL_CALL_TABS = [
  { key: "due_calls", label: "Due Calls", endpoint: WEEKLY_DASH_DUE_CALLS_GRID },
  { key: "active_1", label: "Active 1", endpoint: WEEKLY_DASH_ACTIVE1_GRID },
  { key: "active_2", label: "Active 2", endpoint: WEEKLY_DASH_ACTIVE2_GRID },
];

const BFILE_STAT_KEYS = [
  "total_calls",
  "due_calls",
  "appointments_scheduled",
  "callback_requested",
  "requested_reschedule",
];

const SORT_KEY_MAP = {
  name: "name",
  agency: "agency",
  agency_name: "agency",
  scheduler: "scheduler",
  scheduler_name: "scheduler",
  phone: "phone",
  active_month: "active_month",
  activeMonth: "active_month",
  calls_made: "calls_made",
  callsMade: "calls_made",
  appointment_scheduled: "appointment_scheduled",
  appointmentScheduled: "appointment_scheduled",
  appointment_date: "appointment_date",
  appointmentDate: "appointment_date",
};

const PhoneCellRenderer = (params) => {
  const phone = params.data && params.data.phone;
  if (!phone) return <span>-</span>;

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <a href={`tel:${phone}`} className="phone-link">
        {F.phone_format(phone)}
      </a>
      <Tooltip title="Copy phone number" getPopupContainer={() => document.body}>
        <Icon
          type="copy"
          style={{
            marginLeft: 8,
            cursor: "pointer",
            fontSize: "14px",
            color: "#1890ff",
          }}
          onClick={() => F.handleCopyClick(phone)}
        />
      </Tooltip>
    </div>
  );
};

const SchedulerCellRenderer = (params) => {
  if (!params.data) return null;

  const schedulerLabel = params.value || "—";
  const assignedSchedulers = params.data.assigned_schedulers || [];
  const hasSchedulerAction = Boolean(params.data.scheduler_name_raw);

  if (hasSchedulerAction || assignedSchedulers.length === 0) {
    return (
      <span className="weekly-dashboard-scheduler-cell weekly-dashboard-scheduler-cell--centered">
        {schedulerLabel}
      </span>
    );
  }

  const schedulerNames = assignedSchedulers
    .map((scheduler) =>
      typeof scheduler === "string"
        ? scheduler
        : scheduler && (scheduler.scheduler_name || scheduler.name)
    )
    .filter(Boolean)
    .join(", ");

  if (!schedulerNames) {
    return (
      <span className="weekly-dashboard-scheduler-cell weekly-dashboard-scheduler-cell--centered">
        {schedulerLabel}
      </span>
    );
  }

  return (
    <span className="weekly-dashboard-scheduler-cell weekly-dashboard-scheduler-cell--icon-only">
      <Tooltip title={schedulerNames} getPopupContainer={() => document.body}>
        <Icon
          type="info-circle"
          className="weekly-dashboard-scheduler-info"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
      </Tooltip>
    </span>
  );
};

const DEFAULT_SORT = {
  total_calls: { field: "name", direction: "asc" },
  appointments_scheduled: { field: "appointment_date", direction: "desc" },
  callback_requested: { field: "name", direction: "asc" },
  requested_reschedule: { field: "name", direction: "asc" },
};

const toApiCarrier = (carrier) =>
  !carrier || carrier === "All Carrier" ? "All Carriers" : carrier;

const readStatCard = () => {
  try {
    return JSON.parse(localStorage.getItem("weeklyDashboardStatCard")) || {};
  } catch (err) {
    return {};
  }
};

const formatActiveMonth = (value) => {
  if (value == null || value === "") return "—";
  const text = String(value).trim();
  if (/^\d{1,2}$/.test(text)) {
    const monthIndex = Number(text) - 1;
    return monthIndex >= 0 && monthIndex <= 11
      ? moment().month(monthIndex).format("MMMM")
      : text;
  }

  const parsed = moment(text, ["MMMM", "MMM", "YYYY-MM", "YYYY-MM-DD"], true);
  return parsed.isValid() ? parsed.format("MMMM") : text;
};

const mapBfileRows = (responseData = [], statKey = "") =>
  responseData.map((item, index) => {
    const schedulerRole = item.scheduler_role ? ` (${item.scheduler_role})` : "";
    const schedulerName = item.scheduler_name || "—";
    return {
      id:
        item.appointment_id ||
        item.bfile_id ||
        `${item.agency_id || "agency"}-${index}`,
      bfile_id: item.bfile_id,
      bad_phone_number_status: item.bad_phone_number_status,
      name: item.name || "—",
      phone: item.phone || item.phone_number || "",
      agency: item.agency_name || "—",
      agency_name: item.agency_name || "—",
      agency_id: item.agency_id,
      scheduler: `${schedulerName}`,
      scheduler_name: schedulerName,
      scheduler_name_raw: item.scheduler_name || "",
      scheduler_role: item.scheduler_role || "",
      scheduler_id: item.scheduler_id,
      is_scheduler_mapped: item.is_scheduler_mapped,
      assigned_schedulers:
        item.assigned_schedulers || item.mapped_schedulers || item.schedulers || [],
      activeMonth: formatActiveMonth(
        item.active_month || item.review_month || item.follow_up_month
      ),
      active_month: formatActiveMonth(
        item.active_month || item.review_month || item.follow_up_month
      ),
      callsMade: String(item.calls_made != null ? item.calls_made : 0),
      appointmentScheduled:
        item.appointment_scheduled === true
          ? "Yes"
          : item.appointment_scheduled === false
            ? "No"
            : "—",
      appointmentDate: moment(item.appointment_date).format('MM/DD/YYYY') || "—",
      // appointmentDate: F.formatDateWithCustomerTZ(
      //   item.appointment_date,
      //   item.customer_timezone
      // ),
      appointmentTime: F.formatAppointmentTime(
        item.appointment_time,
        item.customer_timezone
      ),
      customer_timezone: item.customer_timezone,
      type_of_call: item.type_of_call,
      raw: item,
      _statKey: statKey,
    };
  });

const WeeklyDashboardBfileList = (props) => {
  const { RSFSCardFilters, setRSFSCardFilters } = useBoundStore();
  const locationState =
    (props.location && props.location.state) || readStatCard() || {};

  const initialStatKey = locationState.statKey || "";
  const defaultSort = DEFAULT_SORT[initialStatKey] || {
    field: "name",
    direction: "asc",
  };

  const [role, setRole] = useState(
    locationState.role || RSFSCardFilters.selectedAdminToggle || REVIEWADMIN
  );
  const [toggleAdminSwitchRole, setAdminToggleSwitchRole] = useState(
    locationState.toggleAdminSwitchRole || LEAD
  );
  const [selectedMonth] = useState(
    locationState.selectedMonth || moment().format("M")
  );
  const [reportDateRange] = useState(() => {
    if (locationState.start_date && locationState.end_date) {
      return {
        start_date: locationState.start_date,
        end_date: locationState.end_date,
      };
    }
    // Support older saved drill-down state that only contains a month.
    const month = moment().startOf("month").month(Number(selectedMonth) - 1);
    return {
      start_date: month.clone().startOf("month").format("YYYY-MM-DD"),
      end_date: month.clone().endOf("month").format("YYYY-MM-DD"),
    };
  });
  const [selectedCarrier] = useState(
    locationState.selectedCarrier || "All Carrier"
  );
  const [statKey] = useState(initialStatKey);
  const [statTitle] = useState(locationState.statTitle || "B File List");
  const isTotalCalls = statKey === "total_calls";
  const [activeTotalCallsTab, setActiveTotalCallsTab] = useState("due_calls");
  const [tabCounts, setTabCounts] = useState({
    due_calls: Number(locationState.tabCounts && locationState.tabCounts.due_calls) || 0,
    active_1: Number(locationState.tabCounts && locationState.tabCounts.active_1) || 0,
    active_2: Number(locationState.tabCounts && locationState.tabCounts.active_2) || 0,
  });

  const [boardRows, setBoardRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState(
    locationState.searchText || ""
  );
  const [selectedAgency, setSelectedAgency] = useState(
    locationState.selectedAgency
  );
  const [hideBadPhoneNumber, setHideBadPhoneNumber] = useState(
    locationState.hideBadPhoneNumber !== undefined
      ? Boolean(locationState.hideBadPhoneNumber)
      : RSFSCardFilters.hideBadPhoneNumber === undefined
        ? true
        : Boolean(RSFSCardFilters.hideBadPhoneNumber)
  );
  const [agencies, setAgencies] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState(defaultSort.field);
  const [sortDirection, setSortDirection] = useState(defaultSort.direction);
  const [sortOrders, setSortOrders] = useState({
    [defaultSort.field]: defaultSort.direction,
  });

  const resetHandlers = useRef([]);
  const filterTimeout = useRef(null);

  const monthOptions = Array.isArray(F.getMonths()) ? F.getMonths() : [];
  const selectedMonthLabel = useMemo(() => {
    const month = monthOptions.find((m) => String(m.id) === String(selectedMonth));
    return month
      ? month.name
      : moment()
        .month(Number(selectedMonth) - 1)
        .format("MMMM");
  }, [monthOptions, selectedMonth]);

  const typeOfCall =
    role === EA
      ? toggleAdminSwitchRole === LEAD
        ? PROSPECT
        : FOLLOWUP
      : REVIEW;
  const schedulerHeader = role === EA ? FOLLOW_UP_SPECIALIST : REVIEW_SCHEDULER;
  const activeMonthHeader = statKey === "due_calls"
    ? (role === EA ? "Follow-up Active Month" : "Review Active Month")
    : role === EA ? "Follow-up Month" : "Review Month";
  const isAppointmentsScheduled = statKey === "appointments_scheduled";
  // Same detail columns as Due Calls (calls made + appointment scheduled)
  const showCallDetailColumns =
    statKey === "total_calls" ||
    statKey === "due_calls" ||
    statKey === "callback_requested" ||
    statKey === "requested_reschedule";

  const fetchAllRSAgencies = async () => {
    try {
      const response = await axios.get(RS_ALL_AGENCIES_API_URL);
      setAgencies(response.data || []);
    } catch (err) {
      console.error("Error fetching RS agencies:", err);
    }
  };

  const fetchAllFSAgencies = async () => {
    try {
      const response = await axios.get(GET_FS_ALL_AGENCIES);
      setAgencies(response.data || []);
    } catch (err) {
      console.error("Error fetching FS agencies:", err);
    }
  };

  useEffect(() => {
    if (role === REVIEWADMIN) {
      fetchAllRSAgencies();
    } else {
      fetchAllFSAgencies();
    }
  }, [role]);

  const fetchBoardRows = async () => {
    if (!selectedMonth || !selectedCarrier || !statKey) return;
    if (!BFILE_STAT_KEYS.includes(statKey)) return;

    try {
      setLoading(true);
      const apiSortKey = SORT_KEY_MAP[sortField] || sortField || "name";
      const queryParams = new URLSearchParams({
        month: Number(selectedMonth),
        ...reportDateRange,
        type_of_call: typeOfCall,
        carrier: toApiCarrier(selectedCarrier),
        page: currentPage,
        per_page: PAGE_SIZE,
        sort: `${apiSortKey}:${sortDirection || "asc"}`,
        "Time-zone": Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      if (!isTotalCalls) {
        queryParams.append("stat", statKey);
      }

      const searchTerm = (searchText || "").trim();
      if (searchTerm) {
        queryParams.append("search", searchTerm);
      }
      if (selectedAgency) {
        queryParams.append("agency_id", selectedAgency);
      }
      if (hideBadPhoneNumber) {
        queryParams.append("bad_phone_number_status", false);
      }
      if (locationState.schedulerId != null && locationState.schedulerId !== "") {
        queryParams.append("scheduler_id", locationState.schedulerId);
      }

      const activeTab = TOTAL_CALL_TABS.find(
        (tab) => tab.key === activeTotalCallsTab
      );
      const endpoint = isTotalCalls && activeTab
        ? activeTab.endpoint
        : WEEKLY_DASH_STAT_GRID;

      const response = await axios.get(
        `${endpoint}?${queryParams.toString()}`
      );
      const payload = response.data || {};
      const rows = payload.data || [];
      const responseRecordCount =
        payload.total_records != null
          ? Number(payload.total_records)
          : payload.total_count != null
            ? Number(payload.total_count)
            : payload.count != null
              ? Number(payload.count)
              : Number(payload.total_pages) <= 1
                ? rows.length
                : null;
      const paginationCount = responseRecordCount != null
        ? responseRecordCount
        : (payload.total_pages || 0) * PAGE_SIZE;
      setBoardRows(mapBfileRows(rows, statKey));
      setTotalPages(payload.total_pages || 0);
      setTotalRecords(paginationCount);
      if (isTotalCalls && responseRecordCount != null) {
        setTabCounts((counts) => ({
          ...counts,
          [activeTotalCallsTab]: responseRecordCount,
        }));
      }
    } catch (error) {
      console.error("Error fetching weekly dashboard bfile list:", error);
      setBoardRows([]);
      setTotalPages(0);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(
      () => {
        fetchBoardRows();
      },
      searchText ? 400 : 0
    );
    return () => clearTimeout(delay);
  }, [
    reportDateRange,
    selectedMonth,
    selectedCarrier,
    typeOfCall,
    currentPage,
    sortField,
    sortDirection,
    searchText,
    selectedAgency,
    hideBadPhoneNumber,
    activeTotalCallsTab,
    statKey,
    role,
  ]);

  const handleSort = (field) => {
    const apiKey = SORT_KEY_MAP[field] || field;
    setSortField((prevField) => {
      const prevApiKey = SORT_KEY_MAP[prevField] || prevField;
      const newDirection =
        prevApiKey === apiKey && sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
      setSortOrders({ [apiKey]: newDirection });
      return apiKey;
    });
    setCurrentPage(1);
  };

  const handleFilter = (field, value) => {
    setCurrentPage(1);

    if (field === "agency_name" || field === "agency") {
      setSelectedAgency(value || undefined);
      return;
    }

    if (
      field === "name" ||
      field === "scheduler_name" ||
      field === "scheduler"
    ) {
      clearTimeout(filterTimeout.current);
      filterTimeout.current = setTimeout(() => {
        setSearchText(value || "");
      }, 300);
    }
  };

  const handleFilterResetHandler = (resetFn) => {
    resetHandlers.current.push(resetFn);
  };

  const handleBackToDashboard = () => {
    localStorage.removeItem("weeklyDashboardStatCard");
    props.history.push({
      pathname: "/dashboard/monthly-report",
      state: {
        fromStatList: true,
        selectedDateRange: locationState.selectedDateRange !== undefined
          ? locationState.selectedDateRange : null,
        ...reportDateRange,
        role,
        toggleAdminSwitchRole,
        selectedMonth,
        selectedCarrier,
        selectedAgency: locationState.selectedAgency,
        searchText: locationState.selectedScheduler
          ? ""
          : locationState.searchText || "",
        selectedScheduler: locationState.selectedScheduler,
        schedulerId: locationState.schedulerId,
      },
    });
  };

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    setAdminToggleSwitchRole(LEAD);
    setCurrentPage(1);
  };

  const handleTotalCallsTabChange = (tabKey) => {
    setActiveTotalCallsTab(tabKey);
    setCurrentPage(1);
  };

  const badPhoneNumberFilter = (
    <label className="monthly-dashboard-bad-phone-label weekly-dashboard-tabs-bad-phone-label">
      <input
        type="checkbox"
        checked={hideBadPhoneNumber}
        onChange={(event) => {
          const checked = event.target.checked;
          setHideBadPhoneNumber(checked);
          setRSFSCardFilters({ hideBadPhoneNumber: checked ? 1 : 0 });
          setCurrentPage(1);
        }}
      />
      <span style={{ fontWeight: 500, color: 'black' }}>Hide Bad Phone Number</span>
    </label>
  );

  const handleRoleSwitchChange = (fsRole) => {
    setAdminToggleSwitchRole(fsRole);
    setCurrentPage(1);
  };

  const refresh = () => {
    resetHandlers.current.forEach((reset) => reset());
    resetHandlers.current = [];

    setSearchText("");
    setSelectedAgency(undefined);
    setSortField(defaultSort.field);
    setSortDirection(defaultSort.direction);
    setSortOrders({ [defaultSort.field]: defaultSort.direction });
    setCurrentPage(1);
  };

  const CreateNameRendererRS = (params) => {
    if (!params.data) return null;

    const bFileId = params.data.bfile_id;
    const showBN = params.data.bad_phone_number_status === 1;
    const displayName = params.value || params.data.name || "—";

    if (!bFileId) {
      return (
        <div>
          <Icon type="user" style={{ marginRight: 10, color: "#666" }} />
          <span>{displayName}</span>
          {showBN && <BadNumberTooltip />}
        </div>
      );
    }

    return (
      <div>
        <Icon type="user" style={{ marginRight: 10, color: "#666" }} />
        <a
          className="custom-link"
          href={`/customer/${bFileId}`}
          onClick={(e) => {
            e.preventDefault();
            props.history.push(`/customer/${bFileId}`);
          }}
        >
          {displayName}
        </a>
        {showBN && <BadNumberTooltip />}
      </div>
    );
  };

  const columnDefs = useMemo(() => {
    const cols = [
      {
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: ["Name"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "name",
          sortOrder: sortOrders.name,
          handleSort,
          handleFilter,
          placeholder: TYPE_TO_FILTER_BY_NAME,
          showPopOver: true,
          inputType: "text",
          handleFilterResetHandler,
        }),
        field: "name",
        minWidth: 180,
        sortable: true,
        filter: true,
        cellRenderer: "CreateNameRendererRS",
      },
      {
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: ["Phone Number"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "phone",
          sortOrder: sortOrders.phone,
          handleSort,
          showPopOver: false,
        }),
        field: "phone",
        minWidth: 160,
        sortable: true,
        filter: false,
        cellRenderer: "PhoneCellRenderer",
      },
      {
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: ["Agency"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "agency",
          sortOrder: sortOrders.agency,
          handleSort,
          handleFilter,
          showPopOver: true,
          inputType: "select",
          options: agencies,
          handleFilterResetHandler,
        }),
        field: "agency",
        minWidth: isTotalCalls || statKey === "callback_requested" ? 250 : 150,
        sortable: true,
        filter: true,
      },
      {
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: [schedulerHeader],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "scheduler",
          sortOrder: sortOrders.scheduler,
          handleSort,
          handleFilter,
          placeholder: TYPE_TO_FILTER_BY_NAME,
          showPopOver: true,
          inputType: "text",
          handleFilterResetHandler,
        }),
        field: "scheduler",
        minWidth: 200,
        sortable: true,
        filter: true,
        cellRenderer: "SchedulerCellRenderer",
      },
      {
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: [activeMonthHeader],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "active_month",
          sortOrder: sortOrders.active_month,
          handleSort,
          showPopOver: false,
        }),
        field: "activeMonth",
        minWidth: isTotalCalls || statKey === "callback_requested" ? 250 : 150,
        sortable: true,
        filter: false,
      },
    ];

    if (showCallDetailColumns) {
      // cols.push({
      //   headerClass: "custom-ag-header",
      //   headerComponent: "CustomHeader",
      //   headerComponentParams: (params) => ({
      //     headers: ["Calls Made"],
      //     colDef: params.column.getColDef(),
      //     columnApi: params.columnApi,
      //     field: "calls_made",
      //     sortOrder: sortOrders.calls_made,
      //     handleSort,
      //     showPopOver: false,
      //   }),
      //   field: "callsMade",
      //   minWidth: 130,
      //   sortable: true,
      //   filter: false,
      // });
    }

    if (isAppointmentsScheduled) {
      cols.push({
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: ["Appointment Date"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "appointment_date",
          sortOrder: sortOrders.appointment_date,
          handleSort,
          showPopOver: false,
        }),
        field: "appointmentDate",
        minWidth: 180,
        sortable: true,
        filter: false,
      });
      cols.push({
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: ["Appointment Time"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "appointmentTime",
          sortOrder: sortOrders.appointmentTime,
          handleSort,
          showPopOver: false,
        }),
        field: "appointmentTime",
        minWidth: 160,
        sortable: false,
        filter: false,
      });
    }

    if (statKey === "due_calls") {
      return cols
        .filter((column) =>
          ["name", "phone", "agency", "activeMonth"].includes(column.field)
        )
        .map((column) => ({ ...column, flex: 1 }));
    }

    return cols;
  }, [
    sortOrders,
    agencies,
    schedulerHeader,
    activeMonthHeader,
    showCallDetailColumns,
    isAppointmentsScheduled,
    isTotalCalls,
    statKey,
  ]);

  return (
    <div className="monthly-dashboard-page">
      <div className="monthly-dashboard-title">{statTitle}</div>
      <div
        role="button"
        tabIndex="0"
        className="monthly-dashboard-back hyperlink-shomore-stat"
        onClick={handleBackToDashboard}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleBackToDashboard();
          }
        }}
      >
        <Icon type="arrow-left" />
        Back to Dashboard
      </div>

      <CommonHeader
        user={(props.auth && props.auth.user) || props.user || {}}
        onRoleChange={handleRoleChange}
        isHidden={false}
      />

      {role === EA && (
        <div className="monthly-dashboard-actions" style={{ marginTop: 16 }}>
          <Radio.Group
            value={toggleAdminSwitchRole}
            onChange={(e) => handleRoleSwitchChange(e.target.value)}
            className="monthly-dashboard-lead-toggle"
          >
            <Radio.Button
              value={LEAD}
              className={
                toggleAdminSwitchRole === LEAD
                  ? "review-btn-active"
                  : "review-btn-inactive"
              }
              style={{
                width: 120,
                textAlign: "center",
                borderRadius: "20px 0px 0px 20px",
              }}
            >
              {LEAD}
            </Radio.Button>
            <Radio.Button
              value={OPPORTUNITY}
              className={
                toggleAdminSwitchRole === OPPORTUNITY
                  ? "ea-btn-active"
                  : "ea-btn-inactive"
              }
              style={{
                width: 120,
                textAlign: "center",
                borderRadius: "0px 20px 20px 0px",
              }}
            >
              {OPPORTUNITY}
            </Radio.Button>
          </Radio.Group>
        </div>
      )}

      <section className="monthly-dashboard-board" style={{ marginTop: 24 }}>
        {isTotalCalls && (
          <Tabs
            activeKey={activeTotalCallsTab}
            onChange={handleTotalCallsTabChange}
            className="weekly-dashboard-total-calls-tabs"
            tabBarExtraContent={badPhoneNumberFilter}
          >
            {TOTAL_CALL_TABS.map((tab) => (
              <TabPane
                tab={`${tab.label} (${tabCounts[tab.key] || 0})`}
                key={tab.key}
              />
            ))}
          </Tabs>
        )}
        {!isTotalCalls && (
          <div className="weekly-dashboard-tabs-filter-row">
            {badPhoneNumberFilter}
          </div>
        )}
        <div className="monthly-dashboard-board-title">
          <h3>
            <Icon type="table" />
            {statTitle}
          </h3>
          <div className="monthly-dashboard-board-title-actions">
            <Input
              className="monthly-dashboard-board-search"
              placeholder="Search"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
              suffix={<Icon type="search" />}
            />
            <Button
              type="primary"
              className="monthly-dashboard-refresh"
              loading={loading}
              onClick={refresh}
            >
              <Icon type="reload" />
            </Button>
            <span>
              {selectedMonthLabel} · {selectedCarrier}
            </span>
          </div>
        </div>

        <div className="monthly-dashboard-aggrid" style={{ position: "relative" }}>
          <CustomAgGridTable
            rowDatas={boardRows}
            columnDefss={columnDefs}
            frameworkComponents={{
              CustomHeader,
              CreateNameRendererRS,
              PhoneCellRenderer,
              SchedulerCellRenderer,
            }}
          />
          {loading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(255,255,255,0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <Spin tip="Loading ..." />
            </div>
          )}
        </div>

        <div className="monthly-dashboard-pagination">
          <Pagination
            current={currentPage}
            total={totalRecords || totalPages * PAGE_SIZE}
            pageSize={PAGE_SIZE}
            showSizeChanger={false}
            onChange={(page) => setCurrentPage(page)}
          />
        </div>
      </section>
    </div>
  );
};

export default WeeklyDashboardBfileList;



// WEBPACK FOOTER //
// ./src/scenes/Dashboard/parts/review-admin/WeeklyDashboardBfileList.js